import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import Card from '../UI/Card';
import Button from '../UI/Button';
import Input from '../UI/Input';
import Badge from '../UI/Badge';

export default function RecordGenerator() {
  const [students, setStudents] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [formData, setFormData] = useState({
    studentId: '',
    templateId: '',
    semester: '',
    subject: '',
    activityDescription: '',
  });
  const [generating, setGenerating] = useState(false);
  const [generatedRecord, setGeneratedRecord] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [studentsRes, templatesRes] = await Promise.all([
        api.get('/api/user/students'),
        api.get('/api/records/templates/my')
      ]);

      setStudents(studentsRes.data.students || []);
      setTemplates(templatesRes.data.templates || []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTemplateSelect = (template) => {
    setFormData(prev => ({
      ...prev,
      templateId: template.id,
      activityDescription: template.description
    }));
  };

  const handleGenerate = async () => {
    if (!formData.studentId) {
      setError('학생을 선택해주세요.');
      return;
    }

    try {
      setGenerating(true);
      setError(null);

      const payload = {
        studentId: parseInt(formData.studentId),
        templateId: formData.templateId ? parseInt(formData.templateId) : undefined,
        semester: formData.semester || undefined,
        subject: formData.subject || undefined,
        activityDescription: formData.activityDescription || undefined,
      };

      const response = await api.post('/api/records/generate', payload);

      if (response.data.success) {
        setGeneratedRecord(response.data.record);
      }
    } catch (err) {
      setError(err.response?.data?.error || '세특 생성에 실패했습니다.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-neutral-800 mb-2">세특 생성</h1>
        <p className="text-neutral-600">AI를 활용하여 학생부 세부능력 및 특기사항을 자동 생성합니다.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Form */}
        <div className="space-y-6">
          <Card>
            <h2 className="text-xl font-bold text-neutral-800 mb-4">기본 정보</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  학생 선택
                  <span className="text-danger-500 ml-1">*</span>
                </label>
                <select
                  name="studentId"
                  value={formData.studentId}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 rounded-xl border-2 border-neutral-200 focus:border-primary-500 focus:outline-none transition-colors duration-200"
                >
                  <option value="">학생을 선택하세요</option>
                  {students.map((student) => (
                    <option key={student.id} value={student.id}>
                      {student.username} ({student.gradeLevel || '학년 미지정'})
                    </option>
                  ))}
                </select>
              </div>

              <Input
                label="학기"
                name="semester"
                value={formData.semester}
                onChange={handleChange}
                placeholder="예: 2024-1학기"
              />

              <Input
                label="과목"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="예: 과학"
              />
            </div>
          </Card>

          <Card>
            <h2 className="text-xl font-bold text-neutral-800 mb-4">활동 템플릿</h2>

            {templates.length === 0 ? (
              <p className="text-neutral-500 text-sm">저장된 템플릿이 없습니다.</p>
            ) : (
              <div className="space-y-2">
                {templates.map((template) => (
                  <button
                    key={template.id}
                    type="button"
                    onClick={() => handleTemplateSelect(template)}
                    className={`w-full text-left p-3 rounded-xl border-2 transition-colors ${
                      formData.templateId === template.id
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-neutral-200 hover:border-neutral-300'
                    }`}
                  >
                    <p className="font-medium text-neutral-800">{template.title}</p>
                    <p className="text-sm text-neutral-600 mt-1 line-clamp-2">
                      {template.description}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </Card>

          <Card>
            <h2 className="text-xl font-bold text-neutral-800 mb-4">활동 설명</h2>

            <textarea
              name="activityDescription"
              value={formData.activityDescription}
              onChange={handleChange}
              className="w-full px-4 py-2.5 rounded-xl border-2 border-neutral-200 focus:border-primary-500 focus:outline-none transition-colors duration-200 min-h-[150px]"
              placeholder="학생의 활동 내용을 구체적으로 설명해주세요..."
            />
          </Card>

          {error && (
            <div className="p-4 bg-danger-50 border border-danger-200 rounded-xl">
              <p className="text-sm text-danger-600">{error}</p>
            </div>
          )}

          <Button
            onClick={handleGenerate}
            disabled={generating || !formData.studentId}
            className="w-full"
            size="lg"
          >
            {generating ? 'AI가 세특을 생성하는 중...' : '세특 생성하기'}
          </Button>
        </div>

        {/* Generated Record */}
        <div className="space-y-6">
          <Card className="lg:sticky lg:top-6">
            <h2 className="text-xl font-bold text-neutral-800 mb-4">생성된 세특</h2>

            {generatedRecord ? (
              <div className="space-y-4">
                <div className="p-4 bg-secondary-50 rounded-xl">
                  <div className="flex items-center gap-2 mb-3">
                    <Badge variant="success">생성 완료</Badge>
                    <span className="text-sm text-neutral-600">
                      {new Date(generatedRecord.createdAt).toLocaleDateString('ko-KR')}
                    </span>
                  </div>

                  <div className="space-y-2 mb-4">
                    {generatedRecord.student && (
                      <p className="text-sm">
                        <span className="font-medium">학생:</span> {generatedRecord.student.username}
                      </p>
                    )}
                    {generatedRecord.semester && (
                      <p className="text-sm">
                        <span className="font-medium">학기:</span> {generatedRecord.semester}
                      </p>
                    )}
                    {generatedRecord.subject && (
                      <p className="text-sm">
                        <span className="font-medium">과목:</span> {generatedRecord.subject}
                      </p>
                    )}
                  </div>

                  <div className="p-4 bg-white rounded-lg border border-secondary-200">
                    <p className="text-neutral-800 whitespace-pre-wrap leading-relaxed">
                      {generatedRecord.content}
                    </p>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(generatedRecord.content);
                      alert('클립보드에 복사되었습니다.');
                    }}
                    className="flex-1"
                  >
                    복사
                  </Button>
                  <Button
                    onClick={() => window.open(`/api/records/${generatedRecord.id}/export/text`, '_blank')}
                    className="flex-1"
                  >
                    내보내기
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-neutral-500">
                  왼쪽 폼을 작성하고<br />세특 생성하기 버튼을 클릭하세요.
                </p>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
